import Modal from 'components/common/Modal/Modal';
import FormInput from 'components/FormInput';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Stack } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateNewChecklistMutation,
  useGetChecklistsByLibIdMutation,
  useUpdateChecklistsByLibIdMutation,
} from 'redux/services/checklists';
import { ReactComponent as EditIcon } from 'assets/images/edit.svg';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import DeleteIcon from 'assets/images/delete.svg';
import { Question } from 'redux/checklistSlice';
import './ChecklistSetup.scss';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';

const ChecklistSetup = () => {
  const { t } = useTranslation();
  const { libId, checklistId } = useParams();
  const [show, setShow] = useState(checklistId === 'add' ? true : false);
  const [showDeleteQuestionModal, setShowDeleteQuestionModal] = useState(false);
  const [questions, setQuestions] = useState(0);
  const [getChecklistsByLibId] = useGetChecklistsByLibIdMutation();
  const [updateChecklistsByLibId, { isSuccess, isLoading }] =
    useUpdateChecklistsByLibIdMutation();
  const [createNewChecklist] = useCreateNewChecklistMutation();
  const navigate = useNavigate();
  const [withIcon, setWithIcon] = useState(false);
  const [deleteQuestionPos, setDeleteQuestionPos] = useState(0);

  const handleClose = () => {
    setShow(false);
    if (!withIcon && checklistId === 'add') {
      navigate('/checklist');
    }
  };
  const handleShow = () => setShow(true);

  const formik = useFormik({
    initialValues: {
      name: '',
      data: [] as Question[],
    },
    onSubmit: async (val) => {
      const params = {
        libraryId: libId,
        checklistId: checklistId,
        body: {
          ...val,
          libraryId: libId,
        },
      };
      if (checklistId !== 'add') {
        const res: any = await updateChecklistsByLibId(params);
        if (res.data) {
          toast.success('Saved');
          navigate('/checklist');
        } else toast.error(res.error.data.message);
      } else {
        const res: any = await createNewChecklist(params);
        if (res.data) {
          handleClose();
          toast.success('Saved');
          navigate(
            `/checklist/${libId}/${
              res.data.checklists[res.data.checklists.length - 1]._id
            }`,
          );
        } else {
          toast.error(res.error.data.message);
          navigate('/checklist');
        }
      }
    },
  });

  const handleUpdateChecklistName = async () => {
    setShow(false);
    const params = {
      libId: libId,
      checklistId: checklistId,
      values: formik.values,
    };
    if (checklistId !== 'add') {
      const res: any = await updateChecklistsByLibId(params);

      if (res.data) {
        toast.success('Saved');
      } else {
        toast.error(res.error.data.message);
      }
    } else {
      formik.handleSubmit();
    }
  };

  const getChecklistsById = async () => {
    const params = {
      libId: libId,
      checklistId: checklistId,
    };
    const res: any = await getChecklistsByLibId(params);
    if (res.data) {
      formik.setFieldValue('name', res.data.name);
      formik.setFieldValue('data', res.data.data);
      setQuestions(res.data.data.length);
    }
  };

  const handleDeleteQuestion = () => {
    if (formik.values.data.length) {
      let questions = [...formik.values.data];

      questions.splice(deleteQuestionPos, 1);
      formik.setFieldValue('data', questions);
      setQuestions(questions.length);
    } else {
      setQuestions(questions - 1);
    }

    setShowDeleteQuestionModal(false);
  };

  const handleDeleteQuestionModalClose = () => {
    setShowDeleteQuestionModal(false);
  };

  const dragEndHandler = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    let questions = [...formik.values.data];
    const [removed] = questions.splice(result.source.index, 1);
    questions.splice(result.destination.index, 0, removed);
    formik.setFieldValue('data', questions);
  };

  useEffect(() => {
    if (checklistId !== 'add') {
      getChecklistsById();
    }
  }, [isSuccess, checklistId]);

  return (
    <>
      <div className="checklist-setup h-100 d-flex overflow-auto flex-column">
        <div className="page-title pb-3">
          <h2 className="mb-0">
            {checklistId === 'add' ? t('add') : t('edit')} {t('your_checklist')}
          </h2>
        </div>
        <Card className="border-0 flex-grow-1 overflow-auto d-flex flex-column">
          <Card.Body className="pageBg h-100 w-100 d-flex flex-column">
            <div className="d-flex flex-column flex-sm-row align-items-start mb-3 justify-content-between">
              <div className="mb-3 mb-sm-0">
                <Stack direction="horizontal" gap={2}>
                  <h3 className="mb-0" style={{ fontSize: 18 }}>
                    {formik.values.name}
                  </h3>
                  {checklistId !== 'add' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-muted"
                      onClick={() => {
                        setWithIcon(false);
                        handleShow();
                      }}
                    >
                      <Tooltip
                        title={t('edit_checklist_name')}
                        placement="top"
                        arrow
                      >
                        <EditIcon width="16" />
                      </Tooltip>
                    </Button>
                  )}
                </Stack>
                {checklistId !== 'add' && (
                  <p className="mb-0 text-muted">
                    {t('add_checklist_questions')}
                  </p>
                )}
              </div>
              {checklistId === 'add' ? (
                <Button variant="primary" onClick={handleShow}>
                  {t('tooltip_add_checklist')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    setQuestions((prev) => prev + 1);
                    window.scrollTo(0, document.body.scrollHeight);
                  }}
                >
                  {t('add_new_question')}
                </Button>
              )}
            </div>
            <form
              className="questions-form flex-grow-1 d-flex flex-column"
              onSubmit={formik.handleSubmit}
            >
              <div className="d-flex flex-column flex-grow-1 q-container">
                <DragDropContext onDragEnd={dragEndHandler}>
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {[...Array(questions)].map(
                          (item: any, index: number) => (
                            <Draggable
                              key={`question-${index + 1}`}
                              draggableId={`question-${index + 1}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Row
                                  className="mb-3 flex-column flex-md-row"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <Col
                                    style={{ maxWidth: 120 }}
                                    className="d-flex align-items-center mb-2 mb-md-0"
                                  >
                                    <div>
                                      {t('questions')} {index + 1} :
                                    </div>
                                  </Col>
                                  <Col>
                                    <div className="border">
                                      <Form.Control
                                        as="textarea"
                                        name={`data[${index}].question`}
                                        value={
                                          formik.values.data[index]?.question
                                        }
                                        onChange={formik.handleChange}
                                        placeholder="Write here"
                                        className="border-0"
                                        rows={2}
                                      />
                                      <div className="options px-3 py-1 d-flex justify-content-between align-items-center">
                                        <div>
                                          <Form.Check
                                            inline
                                            disabled
                                            label="Yes"
                                            type="radio"
                                          />
                                          <Form.Check
                                            inline
                                            disabled
                                            label="No"
                                            type="radio"
                                          />
                                          <Form.Check
                                            inline
                                            disabled
                                            label="N/A"
                                            type="radio"
                                          />
                                        </div>
                                        <Button
                                          variant="link"
                                          className="text-danger p-1"
                                          size="sm"
                                          onClick={() => {
                                            setDeleteQuestionPos(index);
                                            setShowDeleteQuestionModal(true);
                                          }}
                                        >
                                          <img
                                            src={DeleteIcon}
                                            width="16"
                                            alt=""
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              )}
                            </Draggable>
                          ),
                        )}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
              <Stack className="mt-2" direction="horizontal">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/checklist')}
                >
                  {t('btn_back')}
                </Button>
                <Button
                  variant="primary"
                  className="ms-auto"
                  type="submit"
                  disabled={isLoading}
                >
                  {t('btn_save')}
                </Button>
              </Stack>
            </form>
          </Card.Body>
        </Card>
      </div>
      <Modal
        show={show}
        modalClassName="checklist-setup-modal"
        title={
          checklistId === 'add'
            ? t('tooltip_add_checklist')
            : t('edit_checklist')
        }
        cancelBtnText={t('btn_cancel')}
        submitBtnText={withIcon ? t('btn_delete') : t('btn_save')}
        onSubmit={handleUpdateChecklistName}
        footerContentAlignment={withIcon ? undefined : 'center'}
        onClose={handleClose}
        withIcon={withIcon}
      >
        <FormInput
          label={t('checklist_library_name')}
          value={formik.values.name}
          name="name"
          onChange={formik.handleChange}
          mb="0"
        />
      </Modal>
      <Modal
        show={showDeleteQuestionModal}
        modalClassName="checklist-setup-modal"
        title={t('delete_question')}
        withIcon
        cancelBtnText={t('btn_cancel')}
        submitBtnText={t('btn_delete')}
        onSubmit={handleDeleteQuestion}
        onClose={handleDeleteQuestionModalClose}
      />
    </>
  );
};

export default ChecklistSetup;
